/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { CookieStorage } from 'cookie-storage';
import { User, UserManager } from 'oidc-client-ts';
import { MotorsportsError, MotorsportsErrorType } from 'services/motorsportsError/MotorsportsError';
import { IDENTITY_CONFIG } from './constants';

class AuthService {
  private manager = new UserManager({
    ...IDENTITY_CONFIG,
    response_mode: 'query',
  });

  private user: User | null = null;
  private initialized: boolean = false;
  private isTokenValid: boolean = false;
  private intervalId?: NodeJS.Timeout;
  private cookieStorage = new CookieStorage();

  constructor() {
    this.manager.events.addUserLoaded((user) => {
      this.cookieStorage.setItem('SESSION', user.access_token, { path: '/', sameSite: 'Strict', secure: true });
      this.user = user;
    });
    // Referesh Token every 4.5 minutes
    this.intervalId = setInterval(() => {
      this.signinSilent();
    }, 4.5 * 60 * 1000);

    this.manager.events.addSilentRenewError((error) => {
      if (this.intervalId) clearInterval(this.intervalId);
      new MotorsportsError(error.message, MotorsportsErrorType.Unauthenticated);
    });
  }

  isInitialized = async () => {
    if (!this.initialized) {
      await this.manager.getUser().then(async (user) => {
        if (user) {
          await this.validateToken();
          this.user = this.isTokenValid ? user : null;
        }
        // initialized is set to true once user promise has been returned
        this.initialized = true;
      });
      return this.initialized;
    } else return this.initialized;
  };

  isLoggedin = () => {
    return this.user && !this.user?.expired ? true : false;
  };

  validateToken = async () => {
    return this.manager
      .signinSilent()
      .then((user) => {
        if (user) {
          this.isTokenValid = true;
        } else {
          this.isTokenValid = false;
        }
      })
      .catch(() => (this.isTokenValid = false));
  };

  getUserProfile = () => {
    return this.user?.profile;
  };

  getUsername = () => {
    return this.user?.profile?.preferred_username;
  };

  getEmail = () => {
    return this.user?.profile?.email;
  };

  getAuthorizationHeaderValue = () => {
    return `${this.user?.token_type} ${this.user?.access_token}`;
  };

  getAccessToken = () => {
    return `${this.user?.access_token}`;
  };

  startAuthentication() {
    this.manager.removeUser();
    this.manager.clearStaleState();
    localStorage.setItem('next_url', window.location.pathname + window.location.search);
    return this.manager.signinRedirect();
  }

  completeAuthentication() {
    return this.manager
      .signinRedirectCallback()
      .then((user) => {
        this.user = user;
        return true;
      })
      .catch((error) => {
        console.error(error);
        return false;
      });
  }

  signinSilent = () => {
    this.manager
      .signinSilent()
      .then((user) => {
        this.user = user;
      })
      .catch((error) => {
        new MotorsportsError(error.message, MotorsportsErrorType.Unauthenticated);
      });
  };

  getRoles = (realm: string): string[] => {
    if (this.user) {
      const resources = this.parseJwt(this.user?.access_token)?.resource_access;
      return (resources?.[realm]?.roles as string[]) || [];
    }
    return [];
  };

  getRolesAllRealms = (): string[] => [
    ...this.getRoles(process.env.REACT_APP_ROLE_CLASSIC_REALM.toString()),
    ...this.getRoles(process.env.REACT_APP_ROLE_COMPTOOLS_REALM.toString()),
  ];

  hasRole = (realm: string, role: string): boolean => {
    return this.getRoles(realm).includes(role);
  };

  hasRoleAllRealms = (role: string): boolean => this.getRolesAllRealms().includes(role);

  parseJwt = (token: string) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  };
}

export const authService = new AuthService();
